import React, { useEffect, useRef } from "react";
import { MapPin, Phone, Mail, Facebook, Clock, Star, ExternalLink } from "lucide-react";

const ContactInfoSection = () => {
  const mapRef = useRef(null);

  useEffect(() => {
    // Initialiser la carte Leaflet
    if (typeof window !== 'undefined' && window.L && mapRef.current) {
      // Coordonnées exactes de 31 Rue Notre-Dame, 14570 Clécy
      const lat = 48.9203;
      const lng = -0.4869;
      
      // Créer la carte
      const map = window.L.map(mapRef.current).setView([lat, lng], 15);
      
      // Ajouter les tuiles OpenStreetMap
      window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
      }).addTo(map);
      
      // Ajouter un marqueur personnalisé
      const customIcon = window.L.divIcon({
        html: `<div style="background-color: #1C3A27; width: 24px; height: 24px; border-radius: 50%; border: 3px solid white; box-shadow: 0 3px 6px rgba(0,0,0,0.4); display: flex; align-items: center; justify-content: center;">
                 <div style="color: white; font-size: 12px; font-weight: bold;">🏠</div>
               </div>`,
        iconSize: [30, 30],
        iconAnchor: [15, 15]
      });
      
      window.L.marker([lat, lng], { icon: customIcon })
        .addTo(map)
        .bindPopup(`
          <div style="font-family: Arial, sans-serif; max-width: 200px;">
            <h3 style="margin: 0 0 8px 0; color: #1C3A27; font-size: 16px;">Gîte Cœurs de Normands</h3>
            <p style="margin: 0 0 8px 0; font-size: 13px; color: #666;">31 Rue Notre-Dame<br>14570 Clécy, France</p>
            <p style="margin: 0; font-size: 12px; color: #888;">Gîte de charme 3 clés Clévacances</p>
          </div>
        `)
        .openPopup();
      
      // Nettoyer la carte au démontage du composant
      return () => {
        map.remove();
      };
    }
  }, []);

  const contactInfo = [
    {
      icon: MapPin,
      title: "Adresse",
      content: "31 Rue Notre-Dame, 14570 Clécy, France",
      link: "https://www.google.com/maps/search/?api=1&query=31+Rue+Notre-Dame,+14570+Clécy,+France",
      linkText: "Voir sur Google Maps",
      ariaLabel: "Voir l'adresse sur Google Maps",
      color: "text-blue-600"
    },
    {
      icon: Phone,
      title: "Téléphone",
      content: "+33 6 64 10 48 15",
      link: "tel:+33664104815",
      linkText: "Appeler Wilhelm",
      ariaLabel: "Appeler le numéro de téléphone de Wilhelm",
      color: "text-green-600"
    },
    {
      icon: Mail,
      title: "Email",
      content: "wilhelm.martel@orange.fr",
      link: "mailto:wilhelm.martel@orange.fr",
      linkText: "Envoyer un email",
      ariaLabel: "Envoyer un email à wilhelm.martel@orange.fr",
      color: "text-red-600"
    },
    {
      icon: Clock,
      title: "Disponibilité",
      content: "Réponse rapide\n7j/7",
      multiline: true,
      color: "text-purple-600"
    }
  ];

  return (
    <section 
      id="contact-info" 
      className="section-padding bg-gradient-to-br from-[#FAF9F6] to-[#EDE6DB]/30"
      aria-labelledby="contact-info-title"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* En-tête de section */}
        <div className="text-center mb-12 md:mb-16">
          <h2 
            id="contact-info-title"
            className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-[#1C3A27] mb-4 md:mb-6"
          >
            Nous contacter
          </h2>
          <p className="text-lg md:text-xl text-[#1C3A27]/70 max-w-3xl mx-auto leading-relaxed">
            Nathalie & Wilhelm Martel sont à votre disposition pour toutes vos questions
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 md:gap-12 lg:gap-16">
          {/* Informations de contact */}
          <div className="space-y-6 md:space-y-8 order-2 lg:order-1">
            <div className="grid sm:grid-cols-2 gap-6">
              {contactInfo.map((info, index) => {
                const IconComponent = info.icon;
                return (
                  <div
                    key={index}
                    className="bg-white rounded-xl p-6 shadow-lg border border-[#EDE6DB]/50 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                  >
                    <div className="flex flex-col items-start">
                      <div className={`w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-4`}>
                        <IconComponent size={24} className={info.color} />
                      </div>
                      
                      <h3 className="font-semibold text-lg text-[#1C3A27] mb-2">
                        {info.title}
                      </h3>
                      
                      {info.multiline ? (
                        <p className="text-[#1C3A27]/70 text-sm leading-relaxed whitespace-pre-line">
                          {info.content}
                        </p>
                      ) : (
                        <>
                          <p className="text-[#1C3A27]/70 text-sm leading-relaxed mb-3">
                            {info.content}
                          </p>
                          {info.link && (
                            <a
                              href={info.link}
                              target={info.link.startsWith('http') ? "_blank" : "_self"}
                              rel={info.link.startsWith('http') ? "noopener noreferrer" : undefined}
                              className="text-[#1C3A27] hover:text-[#2A5236] font-medium text-sm underline transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#1C3A27] focus:ring-offset-2 rounded-sm flex items-center"
                              aria-label={info.ariaLabel}
                            >
                              {info.linkText}
                              {info.link.startsWith('http') && <ExternalLink size={14} className="ml-1" />}
                            </a>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Réseaux sociaux amélioré */}
            <div className="bg-white rounded-xl p-6 shadow-lg border border-[#EDE6DB]/50">
              <h3 className="font-semibold text-lg text-[#1C3A27] mb-4 flex items-center">
                <Facebook size={24} className="mr-3 text-blue-600" />
                Suivez-nous sur Facebook
              </h3>
              <div className="space-y-3">
                <a
                  href="https://www.facebook.com/share/1BYJJgudhw/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between p-3 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  aria-label="Suivre sur Facebook - Ouverture dans un nouvel onglet"
                  title="Page Facebook officielle de Cœurs de Normands"
                >
                  <div className="flex items-center">
                    <Facebook size={20} className="mr-3 text-blue-600" />
                    <div>
                      <div className="font-medium text-[#1C3A27]">Cœurs de Normands</div>
                      <div className="text-sm text-[#1C3A27]/70">Page officielle du gîte</div>
                    </div>
                  </div>
                  <ExternalLink size={16} className="text-blue-600" />
                </a>
                <div className="flex items-center text-sm text-[#1C3A27]/60 px-3">
                  <Star size={16} className="mr-2 text-yellow-500" />
                  <span>Avis authentiques de nos clients</span>
                </div>
              </div>
            </div>

            {/* Informations pratiques */}
            <div className="bg-gradient-to-br from-[#1C3A27] to-[#2A5236] rounded-xl p-6 text-white">
              <h3 className="font-serif text-xl font-bold mb-4">
                Informations pratiques
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-white rounded-full mr-3"></div>
                  <span>Check-in: 16h - 20h</span>
                </div>
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-white rounded-full mr-3"></div>
                  <span>Check-out: avant 11h</span>
                </div>
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-white rounded-full mr-3"></div>
                  <span>Parking gratuit</span>
                </div>
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-white rounded-full mr-3"></div>
                  <span>Animaux acceptés</span>
                </div>
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-white rounded-full mr-3"></div>
                  <span>Accès autonome</span>
                </div>
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-white rounded-full mr-3"></div>
                  <span>Ouvert toute l'année</span>
                </div>
              </div>
            </div>
          </div>

          {/* Carte interactive améliorée */}
          <div className="bg-white rounded-xl p-4 shadow-lg border border-[#EDE6DB]/50 order-1 lg:order-2">
            <div className="mb-4">
              <h3 className="font-serif text-xl font-bold text-[#1C3A27] mb-2">
                Localisation - Clécy, Suisse Normande
              </h3>
              <p className="text-sm text-[#1C3A27]/70">
                Cliquez sur le marqueur pour plus d'informations
              </p>
            </div>
            <div 
              ref={mapRef}
              className="w-full h-64 md:h-80 lg:h-96 rounded-lg border border-[#EDE6DB]/30"
              aria-label="Carte interactive montrant la localisation du gîte Cœurs de Normands à Clécy"
            ></div>
            <div className="mt-4 p-3 bg-[#EDE6DB]/30 rounded-lg">
              <p className="text-xs text-[#1C3A27]/60 text-center">
                📍 <strong>31 Rue Notre-Dame, 14570 Clécy</strong> - Au cœur de la Suisse Normande
              </p>
            </div>
          </div>
        </div>

        {/* Langues parlées */}
        <div className="mt-12 text-center">
          <div className="bg-white rounded-xl p-6 shadow-lg border border-[#EDE6DB]/50 max-w-2xl mx-auto">
            <h4 className="font-semibold text-[#1C3A27] mb-3">Langues parlées</h4>
            <div className="flex justify-center space-x-6">
              <div className="flex items-center">
                <span className="text-2xl mr-2">🇫🇷</span>
                <span className="text-[#1C3A27]">Français</span>
                <span className="ml-2 text-sm text-green-600 font-medium">(prioritaire)</span>
              </div>
              <div className="flex items-center">
                <span className="text-2xl mr-2">🇬🇧</span>
                <span className="text-[#1C3A27]">English</span>
                <span className="ml-2 text-sm text-blue-600 font-medium">(bilingue)</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactInfoSection;